'use client';

import { useState, useCallback, useEffect, useRef } from 'react';

export type ResizeDirection = 'nw' | 'ne' | 'sw' | 'se' | 'n' | 's' | 'e' | 'w';

export interface ResizeArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ResizeConstraints {
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  aspectRatio?: number;
  bounds?: { width: number; height: number };
}

export interface UseResizeHandlesOptions {
  initialArea: ResizeArea;
  constraints?: ResizeConstraints;
  displayScale?: number;
  onAreaChange?: (area: ResizeArea) => void;
  onResizeStart?: () => void;
  onResizeEnd?: () => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}

export interface UseResizeHandlesReturn {
  area: ResizeArea;
  isResizing: ResizeDirection | null;
  isDragging: boolean;
  setArea: (area: ResizeArea) => void;
  handleResizeStart: (e: React.MouseEvent, direction: ResizeDirection) => void;
  handleTouchResizeStart: (
    e: React.TouchEvent,
    direction: ResizeDirection
  ) => void;
  handleDragStart: (e: React.MouseEvent) => void;
  handleTouchDragStart: (e: React.TouchEvent) => void;
  handleTouchMove: (e: React.TouchEvent) => void;
  handleTouchEnd: () => void;
  constrainArea: (area: ResizeArea) => ResizeArea;
}

const DEFAULT_MIN_SIZE = 50;

export function useResizeHandles(
  options: UseResizeHandlesOptions
): UseResizeHandlesReturn {
  const {
    initialArea,
    constraints = {},
    displayScale = 1,
    onAreaChange,
    onResizeStart,
    onResizeEnd,
    onDragStart,
    onDragEnd,
  } = options;

  const {
    minWidth = DEFAULT_MIN_SIZE,
    minHeight = DEFAULT_MIN_SIZE,
    maxWidth,
    maxHeight,
    aspectRatio,
    bounds,
  } = constraints;

  const [area, setAreaState] = useState<ResizeArea>(initialArea);
  const [isResizing, setIsResizing] = useState<ResizeDirection | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Pinch zoom state for touch
  const [pinchStartDistance, setPinchStartDistance] = useState<number | null>(
    null
  );
  const [pinchStartArea, setPinchStartArea] = useState<ResizeArea | null>(null);

  const areaRef = useRef(area);
  const isResizingRef = useRef(isResizing);
  const isDraggingRef = useRef(isDragging);
  const dragStartRef = useRef(dragStart);

  useEffect(() => {
    areaRef.current = area;
  }, [area]);

  useEffect(() => {
    isResizingRef.current = isResizing;
  }, [isResizing]);

  useEffect(() => {
    isDraggingRef.current = isDragging;
  }, [isDragging]);

  useEffect(() => {
    dragStartRef.current = dragStart;
  }, [dragStart]);

  const constrainArea = useCallback(
    (newArea: ResizeArea): ResizeArea => {
      const constrained = { ...newArea };

      // Ensure minimum size
      constrained.width = Math.max(constrained.width, minWidth);
      constrained.height = Math.max(constrained.height, minHeight);

      // Apply maximum size
      if (maxWidth) {
        constrained.width = Math.min(constrained.width, maxWidth);
      }
      if (maxHeight) {
        constrained.height = Math.min(constrained.height, maxHeight);
      }

      // Maintain aspect ratio if specified
      if (aspectRatio) {
        const currentRatio = constrained.width / constrained.height;
        if (currentRatio > aspectRatio) {
          constrained.width = constrained.height * aspectRatio;
        } else {
          constrained.height = constrained.width / aspectRatio;
        }
      }

      // Ensure area stays within bounds
      if (bounds) {
        constrained.x = Math.max(
          0,
          Math.min(constrained.x, bounds.width - constrained.width)
        );
        constrained.y = Math.max(
          0,
          Math.min(constrained.y, bounds.height - constrained.height)
        );
        constrained.width = Math.min(
          constrained.width,
          bounds.width - constrained.x
        );
        constrained.height = Math.min(
          constrained.height,
          bounds.height - constrained.y
        );
      }

      return constrained;
    },
    [minWidth, minHeight, maxWidth, maxHeight, aspectRatio, bounds]
  );

  const setArea = useCallback(
    (newArea: ResizeArea) => {
      const constrained = constrainArea(newArea);
      setAreaState(constrained);
      onAreaChange?.(constrained);
    },
    [constrainArea, onAreaChange]
  );

  const calculateNewArea = useCallback(
    (
      direction: ResizeDirection,
      prevArea: ResizeArea,
      deltaX: number,
      deltaY: number
    ): ResizeArea => {
      const newArea = { ...prevArea };

      switch (direction) {
        case 'nw':
          newArea.x += deltaX;
          newArea.y += deltaY;
          newArea.width -= deltaX;
          newArea.height -= deltaY;
          break;
        case 'ne':
          newArea.y += deltaY;
          newArea.width += deltaX;
          newArea.height -= deltaY;
          break;
        case 'sw':
          newArea.x += deltaX;
          newArea.width -= deltaX;
          newArea.height += deltaY;
          break;
        case 'se':
          newArea.width += deltaX;
          newArea.height += deltaY;
          break;
        case 'n':
          newArea.y += deltaY;
          newArea.height -= deltaY;
          break;
        case 's':
          newArea.height += deltaY;
          break;
        case 'w':
          newArea.x += deltaX;
          newArea.width -= deltaX;
          break;
        case 'e':
          newArea.width += deltaX;
          break;
      }

      return newArea;
    },
    []
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const deltaX = (e.clientX - dragStartRef.current.x) / displayScale;
      const deltaY = (e.clientY - dragStartRef.current.y) / displayScale;

      if (isResizingRef.current) {
        const newArea = calculateNewArea(
          isResizingRef.current,
          areaRef.current,
          deltaX,
          deltaY
        );
        setArea(newArea);
      } else if (isDraggingRef.current) {
        setArea({
          ...areaRef.current,
          x: areaRef.current.x + deltaX,
          y: areaRef.current.y + deltaY,
        });
      }

      setDragStart({ x: e.clientX, y: e.clientY });
    },
    [displayScale, calculateNewArea, setArea]
  );

  const handleMouseUp = useCallback(() => {
    if (isResizingRef.current) {
      setIsResizing(null);
      onResizeEnd?.();
    }
    if (isDraggingRef.current) {
      setIsDragging(false);
      onDragEnd?.();
    }
  }, [onResizeEnd, onDragEnd]);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent, direction: ResizeDirection) => {
      e.preventDefault();
      e.stopPropagation();

      setIsResizing(direction);
      setDragStart({ x: e.clientX, y: e.clientY });
      onResizeStart?.();
    },
    [onResizeStart]
  );

  const handleTouchResizeStart = useCallback(
    (e: React.TouchEvent, direction: ResizeDirection) => {
      e.preventDefault();
      e.stopPropagation();

      if (e.touches.length !== 1) return;

      const touch = e.touches[0];
      if (!touch) return;

      setIsResizing(direction);
      setDragStart({ x: touch.clientX, y: touch.clientY });
      onResizeStart?.();
    },
    [onResizeStart]
  );

  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
      onDragStart?.();
    },
    [onDragStart]
  );

  const handleTouchDragStart = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (e.touches.length === 1) {
        const touch = e.touches[0];
        if (!touch) return;

        setIsDragging(true);
        setDragStart({ x: touch.clientX, y: touch.clientY });
        onDragStart?.();
      } else if (e.touches.length === 2) {
        // Pinch to resize from center
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        if (touch1 && touch2) {
          const distance = Math.hypot(
            touch2.clientX - touch1.clientX,
            touch2.clientY - touch1.clientY
          );
          setPinchStartDistance(distance);
          setPinchStartArea({ ...areaRef.current });
          setIsDragging(false);
          setIsResizing(null);
        }
      }
    },
    [onDragStart]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault();

      if (
        e.touches.length === 1 &&
        (isDraggingRef.current || isResizingRef.current)
      ) {
        const touch = e.touches[0];
        if (!touch) return;

        const deltaX = (touch.clientX - dragStartRef.current.x) / displayScale;
        const deltaY = (touch.clientY - dragStartRef.current.y) / displayScale;

        if (isDraggingRef.current) {
          setArea({
            ...areaRef.current,
            x: areaRef.current.x + deltaX,
            y: areaRef.current.y + deltaY,
          });
        } else if (isResizingRef.current) {
          const newArea = calculateNewArea(
            isResizingRef.current,
            areaRef.current,
            deltaX,
            deltaY
          );
          setArea(newArea);
        }

        setDragStart({ x: touch.clientX, y: touch.clientY });
      } else if (
        e.touches.length === 2 &&
        pinchStartDistance &&
        pinchStartArea
      ) {
        // Pinch to resize
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        if (touch1 && touch2) {
          const currentDistance = Math.hypot(
            touch2.clientX - touch1.clientX,
            touch2.clientY - touch1.clientY
          );

          const scale = currentDistance / pinchStartDistance;

          // Calculate new size
          const newWidth = pinchStartArea.width * scale;
          const newHeight = pinchStartArea.height * scale;

          // Scale from center
          const centerX = pinchStartArea.x + pinchStartArea.width / 2;
          const centerY = pinchStartArea.y + pinchStartArea.height / 2;

          setArea({
            x: centerX - newWidth / 2,
            y: centerY - newHeight / 2,
            width: newWidth,
            height: newHeight,
          });
        }
      }
    },
    [
      displayScale,
      calculateNewArea,
      setArea,
      pinchStartDistance,
      pinchStartArea,
    ]
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    setIsResizing(null);
    setPinchStartDistance(null);
    setPinchStartArea(null);
    onDragEnd?.();
    onResizeEnd?.();
  }, [onDragEnd, onResizeEnd]);

  // Global mouse events for resizing/dragging
  useEffect(() => {
    if (isResizing || isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
    return undefined;
  }, [isResizing, isDragging, handleMouseMove, handleMouseUp]);

  return {
    area,
    isResizing,
    isDragging,
    setArea,
    handleResizeStart,
    handleTouchResizeStart,
    handleDragStart,
    handleTouchDragStart,
    handleTouchMove,
    handleTouchEnd,
    constrainArea,
  };
}
